import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { UserAuthService } from '../UserAuth.service';
import { ProjectService } from '../services/project.service';
import { VericureUser } from '../models/vericure-user';

/** Outcome of establishing the signed-in user's roles. */
export type RolesOutcome = 'known' | 'denied' | 'unavailable' | 'no-account';

/**
 * Establish the user's roles BEFORE anything is decided on them.
 *
 * THE RACE THIS CLOSES. CheckUserExists ran in exactly one place: the /login
 * component. Any other route into the app arrived with no roles known:
 *   - Entra's redirect lands on `/` on both deployed hosts;
 *   - a deep link opened while MSAL was already signed in, with no cached roles.
 * The guard read "no roles" as "no company needed", let MainLayout load, and
 * MainLayout called GetProjectlist straight away with no customerId. With
 * DataServices ff2f917 that call is a 403 company_selection_required for
 * SuperAdmin and administrator. Before it, the list came back unscoped.
 *
 * SINGLE-FLIGHT. The guard runs on every protected navigation. Concurrent
 * callers share one CheckUserExists request, and a settled answer is not asked
 * again: roles are then held by UserAuthService, and ensureRoles() returns
 * 'known' without a request.
 */
@Injectable({ providedIn: 'root' })
export class RolesResolverService {
  private inFlight: Promise<RolesOutcome> | null = null;

  constructor(
    private readonly auth: UserAuthService,
    private readonly projects: ProjectService
  ) {}

  ensureRoles(): Promise<RolesOutcome> {
    if (this.auth.getRoles().length > 0) {
      return Promise.resolve('known');
    }
    if (!this.inFlight) {
      this.inFlight = this.resolve().finally(() => {
        this.inFlight = null;
      });
    }
    return this.inFlight;
  }

  private async resolve(): Promise<RolesOutcome> {
    // The SESSION account, not only the workforce one: an External ID user has no workforce
    // account, and reading only that left them at 'no-account' with no roles (sme-vericure-webui).
    const account = this.auth.getSessionAccountInfo();
    if (!account?.localAccountId) {
      return 'no-account';
    }
    // The same identity pair /login sends, so both routes ask the same question.
    const result = await firstValueFrom(
      this.projects.checkUserAuthorization(account.tenantId, account.localAccountId)
    );
    if (result.kind === 'authorized') {
      this.auth.setRolesFromCosmos(result.user as VericureUser);
      return this.auth.getRoles().length > 0 ? 'known' : 'denied';
    }
    if (result.kind === 'denied') {
      this.auth.setRolesFromCosmos(null);
      return 'denied';
    }
    // UNAVAILABLE is not a verdict: write nothing, so the next attempt asks again.
    return 'unavailable';
  }
}
